// ---------------------------------------------------------------------------
// EffectiveConfigView — Read-only display of effective configuration
// ---------------------------------------------------------------------------

import { Check, Minus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { EffectiveTenantConfig } from '@/types/config';
import type { CallWindow } from '@/types/config';

function formatTime(hour: number, minute: number): string {
  const period = hour >= 12 ? 'PM' : 'AM';
  const h = hour % 12 || 12;
  const m = minute.toString().padStart(2, '0');
  return `${h}:${m} ${period}`;
}

function formatWindow(w: CallWindow): string {
  return `${formatTime(w.fromHour, w.fromMinute)} \u2013 ${formatTime(w.toHour, w.toMinute)}`;
}

function BoolIndicator({ value }: { value: boolean }) {
  return value ? (
    <Check className="inline h-4 w-4 text-green-600" />
  ) : (
    <Minus className="inline h-4 w-4 text-silver-400" />
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-silver-100 last:border-0">
      <span className="text-sm text-carbon-600">{label}</span>
      <span className="text-sm font-medium text-carbon-900">{children}</span>
    </div>
  );
}

const WINDOW_LABELS: Record<string, string> = {
  regular: 'Regular',
  event: 'Event-Triggered',
  alert: 'Alert-Triggered',
  scheduled: 'Scheduled',
};

interface EffectiveConfigViewProps {
  config: EffectiveTenantConfig;
}

export function EffectiveConfigView({ config }: EffectiveConfigViewProps) {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">General</CardTitle>
        </CardHeader>
        <CardContent>
          <Row label="IVR Enabled"><BoolIndicator value={config.ivrEnabled} /></Row>
          <Row label="No Weekend Calls"><BoolIndicator value={config.noWeekendCalls} /></Row>
          <Row label="Max Questions per Call">{config.maxQuestionsInCall}</Row>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Call Windows</CardTitle>
        </CardHeader>
        <CardContent>
          {(Object.entries(config.callWindows) as [string, CallWindow][]).map(
            ([key, window]) => (
              <Row key={key} label={WINDOW_LABELS[key] ?? key}>
                {formatWindow(window)}
              </Row>
            ),
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Retry Policy</CardTitle>
        </CardHeader>
        <CardContent>
          <Row label="Same-Day Attempts">{config.retryConfig.callBackAttempts}</Row>
          <Row label="Same-Day Interval">{config.retryConfig.callBackInterval} min</Row>
          <Row label="Next-Day Attempts">{config.retryConfig.nextDayCallBackAttempts}</Row>
          <Row label="Next-Day Interval">{config.retryConfig.nextDayCallBackInterval} min</Row>
        </CardContent>
      </Card>
    </div>
  );
}
